import isEqual from "lodash/isEqual";
import React, { useReducer, useCallback, useMemo } from "react";
import RightSidebarContext from "contexts/RightSidebarContext";
const MAX_HISTORY_DEPTH = 20;
const initialState = {
  contentHistory: [],
  currentIndex: -1,
  isOpen: false,
  isCollapsed: false,
  width: 400
};
const isSameContent = (content1, content2) => {
  if (content1.id !== content2.id || !isEqual(content1.props, content2.props)) {
    return false;
  }
  if (content1.componentKey && content2.componentKey) {
    return content1.componentKey === content2.componentKey;
  }
  return content1.component === content2.component;
};
const historyReducer = (state, action) => {
  switch (action.type) {
    case "OPEN_SIDEBAR": {
      if (state.currentIndex >= 0 && isSameContent(state.contentHistory[state.currentIndex], action.content)) {
        const newHistory2 = [...state.contentHistory];
        newHistory2[state.currentIndex] = action.content;
        return {
          ...state,
          contentHistory: newHistory2,
          isOpen: true,
          isCollapsed: false
        };
      }
      const newHistory = state.contentHistory.slice(0, state.currentIndex + 1);
      newHistory.push(action.content);
      if (newHistory.length > MAX_HISTORY_DEPTH) {
        newHistory.shift();
        return {
          ...state,
          contentHistory: newHistory,
          currentIndex: MAX_HISTORY_DEPTH - 1,
          isOpen: true,
          isCollapsed: false
        };
      }
      return {
        ...state,
        contentHistory: newHistory,
        currentIndex: newHistory.length - 1,
        isOpen: true,
        isCollapsed: false
      };
    }
    case "CLOSE_SIDEBAR": {
      return {
        ...state,
        contentHistory: [],
        currentIndex: -1,
        isOpen: false,
        isCollapsed: false
      };
    }
    case "COLLAPSE_SIDEBAR": {
      return {
        ...state,
        isCollapsed: true
      };
    }
    case "EXPAND_SIDEBAR": {
      return {
        ...state,
        isCollapsed: false
      };
    }
    case "UPDATE_CONTENT": {
      if (state.currentIndex === -1) {
        return state;
      }
      const newHistory = [...state.contentHistory];
      newHistory[state.currentIndex] = action.content;
      return {
        ...state,
        contentHistory: newHistory
      };
    }
    case "GO_BACK": {
      if (state.currentIndex <= 0) {
        return state;
      }
      return {
        ...state,
        currentIndex: state.currentIndex - 1
      };
    }
    case "GO_FORWARD": {
      if (state.currentIndex >= state.contentHistory.length - 1) {
        return state;
      }
      return {
        ...state,
        currentIndex: state.currentIndex + 1
      };
    }
    case "SET_WIDTH": {
      return {
        ...state,
        width: action.width
      };
    }
    default:
      return state;
  }
};
const RightSidebarProvider = ({ children }) => {
  const [state, dispatch] = useReducer(historyReducer, initialState);
  const content = state.currentIndex >= 0 ? state.contentHistory[state.currentIndex] : null;
  const canGoBack = state.currentIndex > 0;
  const canGoForward = state.currentIndex < state.contentHistory.length - 1;
  const openSidebar = useCallback((newContent) => {
    dispatch({ type: "OPEN_SIDEBAR", content: newContent });
  }, []);
  const closeSidebar = useCallback(() => {
    dispatch({ type: "CLOSE_SIDEBAR" });
  }, []);
  const collapseSidebar = useCallback(() => {
    dispatch({ type: "COLLAPSE_SIDEBAR" });
  }, []);
  const expandSidebar = useCallback(() => {
    dispatch({ type: "EXPAND_SIDEBAR" });
  }, []);
  const updateContent = useCallback((newContent) => {
    dispatch({ type: "UPDATE_CONTENT", content: newContent });
  }, []);
  const setWidth = useCallback((newWidth) => {
    dispatch({ type: "SET_WIDTH", width: newWidth });
  }, []);
  const goBack = useCallback(() => {
    dispatch({ type: "GO_BACK" });
  }, []);
  const goForward = useCallback(() => {
    dispatch({ type: "GO_FORWARD" });
  }, []);
  const contextValue = useMemo(
    () => ({
      isOpen: state.isOpen,
      isCollapsed: state.isCollapsed,
      content,
      width: state.width,
      openSidebar,
      closeSidebar,
      collapseSidebar,
      expandSidebar,
      updateContent,
      setWidth,
      goBack,
      goForward,
      canGoBack,
      canGoForward
    }),
    [
      state.isOpen,
      state.isCollapsed,
      content,
      state.width,
      openSidebar,
      closeSidebar,
      collapseSidebar,
      expandSidebar,
      updateContent,
      setWidth,
      goBack,
      goForward,
      canGoBack,
      canGoForward
    ]
  );
  return /* @__PURE__ */ React.createElement(RightSidebarContext.Provider, { value: contextValue }, children);
};
export default RightSidebarProvider;
